import React, { FC } from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import {
	cycleRunsRestCrudThunksForBuyer,
	cycleRunsRestCrudThunksForSupplier,
	refreshCycleRunCounts,
} from '../../thunks/cycle_runs_thunks';
import {
	getEntityById,
	getStatusLabelFromPipelineFilters,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { EmptyState } from '../empty_state/EmptyState';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import { CYCLE_RUNS_CRUD_ACTION_CREATORS } from '../../actions/cycle_runs_actions';
import { ROLE_TYPES } from '../../utils/DataConstants';
import CycleRunPipeLineFilters from './CycleRunPipeLineFilters';
import GeneralStatusDisplay from '../AdminCycleEventsIndexPage/GeneralStatusDisplay';
import { ACC_RUN_STATUSES, getCycleRunCounts } from '../../utils/AssetCycleUtils';
import { Button } from 'antd';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import { withUserType } from '../withUserType';

const ASSET_CYCLE_RUNS_TARGET_COLLECTION = 'AssetCycleRunsIndex';
const DEFAULT_BACK_LINK_TEXT = 'Back to Audits';
interface AssetCycleEventDetailDetailsPageProps {
	match: any;
	cycleRuns: any[];
	updateFilters: () => void;
	clearAndUpdateFilters: () => void;
	refreshCounts: () => void;
	location: any;
	history: any;
	userType: string;
}

const AssetCycleEventDetailDetailsPage: FC<AssetCycleEventDetailDetailsPageProps> = ({
	match,
	cycleRuns,
	updateFilters,
	clearAndUpdateFilters,
	refreshCounts,
	userType,
	location,
	history,
}): React.ReactElement => {
	const getAssetLoggedText = (record) => {
		const { totalAssets, assetsLogged, newAssetAdded, transferCount } = getCycleRunCounts(record);

		const renderAdditionalDetails = (primaryText, secondaryText) => (
			<div className="flex flex-row items-center gap-2">
				<div className="bodyregular">{primaryText}</div>
				<div className="bodymedium">{secondaryText}</div>
			</div>
		);

		switch (record.status) {
			case ACC_RUN_STATUSES.IN_PROGRESS:
				return renderAdditionalDetails(
					'Assets Logged:',
					<>
						{assetsLogged}/{totalAssets}{' '}
						{transferCount > 0 || newAssetAdded > 0 ? (
							<>
								{'['}
								{transferCount > 0 && `${transferCount} transfer${transferCount !== 1 ? 's' : ''}`}
								{transferCount > 0 && newAssetAdded > 0 && ', '}
								{newAssetAdded > 0 && `${newAssetAdded} new`}
								{']'}
							</>
						) : (
							''
						)}
					</>
				);

			case ACC_RUN_STATUSES.READY_FOR_APPROVAL:
				return renderAdditionalDetails(
					'Assets Approved:',
					`${nullSafeGetOrElse('approvedAuditCount', record, 0)}/${nullSafeGetOrElse(
						'totalAssetAuditCount',
						record,
						0
					)}`
				);
			case ACC_RUN_STATUSES.APPROVED:
				return renderAdditionalDetails(
					'Changes Applied:',
					`${nullSafeGetOrElse('changesAppliedCount', record, 0)}/${nullSafeGetOrElse(
						'totalAssetAuditCount',
						record,
						0
					)}`
				);
			default:
				return '';
		}
	};

	const columns = [
		{
			title: 'Location',
			dataIndex: ['location', 'name'],
			defaultSortOrder: 'ascend',
			sorter: true,
		},
		{
			title: 'Status',
			dataIndex: 'status',
			defaultSortOrder: 'ascend',
			render: (status, record) => (
				<div className="flex flex-row items-center gap-2">
					<GeneralStatusDisplay
						statusKey={status}
						statusLabel={getStatusLabelFromPipelineFilters(CycleRunPipeLineFilters, status)}
					/>
					{getAssetLoggedText(record)}
				</div>
			),
			sorter: true,
		},
		{
			title: 'Actions',
			dataIndex: 'status',
			render: (status) => {
				switch (status) {
					case ACC_RUN_STATUSES.READY_FOR_APPROVAL:
						return (
							<Button ghost type="primary">
								Approve All
							</Button>
						);
					case ACC_RUN_STATUSES.APPROVED:
						return (
							<Button ghost type="primary">
								Apply All Changes
							</Button>
						);
					default:
						return null;
				}
			},
		},
	];

	const onRow = (record) => ({
		onClick: () => {
			const path = `/${userType}/assetCycleRuns/detail/${record.id}/details`;
			const backUrl = getLinkWIthBackLinkParams(location, DEFAULT_BACK_LINK_TEXT, path);
			history.push(backUrl);
		},
	});

	return (
		<PaginatedReduxTableWithHeader
			targetCollectionName={ASSET_CYCLE_RUNS_TARGET_COLLECTION}
			stateSlice={cycleRuns}
			entityCollectionName="cycle_runs"
			updateFilters={updateFilters}
			clearAndUpdateFilters={clearAndUpdateFilters}
			handleQueryParams
			refreshCounts={refreshCounts}
			pipeLineFilters={CycleRunPipeLineFilters}
			filterConfig={[]}
			updateQueryParams={true}
			emptyState={
				<EmptyState
					graphic={
						<img
							alt="Currently there are no assets!"
							style={{ marginBottom: 8 }}
							src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
						/>
					}
					headline={"It's a little lonely in here."}
					body={
						<div style={{ textAlign: 'center' }}>
							<div style={{ maxWidth: 440, marginBottom: 16 }}>
								Looks like your team has not added any cycle runs yet.
							</div>
						</div>
					}
				/>
			}
			onTableRow={onRow}
			showHeader
			tableColumns={columns}
			preAppliedFilters={{ assetCycleCountEventId: match.params.id }}
			fetchData={
				userType === ROLE_TYPES.SUPPLIER
					? cycleRunsRestCrudThunksForSupplier.read
					: cycleRunsRestCrudThunksForBuyer.read
			}
		/>
	);
};

const mapStateToProps = (state, ownProps) => ({
	cycleEvent: getEntityById(ownProps.match.params.id, state.cycle_events),
	cycleRuns: state.cycle_runs,
	fetching: state.cycle_events.fetching,
	match: ownProps.match,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateFilters: (filters, targetCollection) =>
		dispatch(CYCLE_RUNS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(CYCLE_RUNS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(filters, targetCollectionName)),
	refreshCounts: (filters) => dispatch(refreshCycleRunCounts(ownProps.userType)(filters)),
});

export default withRouter(
	withUserType(
		connect(mapStateToProps, mapDispatchToProps)(AssetCycleEventDetailDetailsPage as any)
	)
);
